"use client";

import { Workflow, Sparkles, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const ArticleProcess = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [showCursor, setShowCursor] = useState(false);
  const textRef = useRef(null);

  const processSteps = [
    {
      step: "01",
      percentage: "Step 1",
      title: "Register",
      description: "Express your interest through a quick application form",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
    },
    {
      step: "02",
      percentage: "Step 2",
      title: "Screening",
      description:
        "Our team carefully reviews applications to ensure the best fit",
      color: "from-indigo-500 to-indigo-600",
      bgColor: "bg-indigo-500/10",
      borderColor: "border-indigo-500/30",
    },
    {
      step: "03",
      percentage: "Step 3",
      title: "Shortlisting",
      description:
        "Selected students receive exclusive payment links to secure their spot",
      color: "from-emerald-500 to-emerald-600",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/30",
    },
  ];

  const cohortText = "Cohort Strength: Handpicking 10 student";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          setShowCursor(true);

          // Typewriter effect
          let currentIndex = 0;
          const typeInterval = setInterval(() => {
            if (currentIndex <= cohortText.length) {
              setDisplayedText(cohortText.slice(0, currentIndex));
              currentIndex++;
            } else {
              clearInterval(typeInterval);
              // Hide cursor after typing is complete
              setTimeout(() => setShowCursor(false), 1000);
            }
          }, 80); // 80ms delay between each character
        }
      },
      { threshold: 0.3 }
    );

    if (textRef.current) {
      observer.observe(textRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible, cohortText]);

  return (
    <div className="bg-black py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Motivational Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
            <Workflow className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-blue-400 tracking-wide uppercase">
              How does it work
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl  font-bold text-white mb-2 leading-tight mt-12">
            Stop wishing and{" "}
            <span className="text-transparent bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text">
              start doing.
            </span>
          </h2>
        </div>

        {/* Engaging Call-to-Action Element */}
        <div className="relative mb-12">
          <div className="text-center">
            <p className="text-gray-400 text-lg  mx-auto md:whitespace-nowrap">
              These will be your exact steps once you make up your mind to join
              the elite 10
            </p>
          </div>
        </div>

        {/* Process Steps Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {processSteps.map((step, index) => (
            <div
              key={index}
              className={`group relative bg-gray-900/80 backdrop-blur-sm border ${step.borderColor} rounded-xl p-6 hover:border-opacity-70 transition-all duration-300 hover:transform hover:-translate-y-1`}
            >
              {/* Background gradient overlay */}
              <div
                className={`absolute inset-0 ${step.bgColor} rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              ></div>

              <div className="relative z-10">
                {/* Step indicator */}
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={`text-4xl md:text-5xl font-black text-transparent bg-gradient-to-r ${step.color} bg-clip-text`}
                  >
                    {step.step}
                  </div>
                  <div
                    className={`px-3 py-1 bg-gradient-to-r ${step.color} rounded-full text-white text-sm font-semibold`}
                  >
                    {step.percentage}
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <h4 className="text-xl md:text-2xl font-bold text-white group-hover:text-gray-100 transition-colors">
                    {step.title}
                  </h4>
                  <p className="text-gray-400 group-hover:text-gray-200 transition-colors leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Decorative element */}
                <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <div
                    className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-full blur-xl`}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Stats with Typewriter Animation */}
        <div className="text-center" ref={textRef}>
          <div className="inline-flex flex-col sm:flex-row items-center justify-center bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg px-3 py-4 sm:px-6 sm:py-3 max-w-full">
            {/* Mobile: Stack vertically, Desktop: Single line */}
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <span className="text-gray-400 text-sm sm:text-lg font-mono text-center">
                {displayedText.split("").map((char, index) => {
                  const colonIndex = cohortText.indexOf(":");
                  const isBeforeColon = index <= colonIndex;
                  const isNumber = /\d/.test(char);
                  const isHandpicking =
                    index >= cohortText.indexOf("Handpicking") &&
                    index <
                      cohortText.indexOf("Handpicking") + "Handpicking".length;

                  // Split at colon for mobile
                  if (char === ":" && isBeforeColon) {
                    return (
                      <span key={index} className="inline-block">
                        <span className="text-gray-400">:</span>
                        <span className="block sm:hidden w-full h-2"></span>
                      </span>
                    );
                  }

                  // Skip rendering space right after colon on mobile
                  if (index === colonIndex + 1 && char === " ") {
                    return (
                      <span
                        key={index}
                        className="hidden sm:inline-block w-2"
                      ></span>
                    );
                  }

                  if (char === " ") {
                    return (
                      <span
                        key={index}
                        className="inline-block w-1 sm:w-2"
                      ></span>
                    );
                  }

                  return (
                    <span
                      key={index}
                      className={`inline-block ${
                        isNumber || isHandpicking
                          ? "text-transparent bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text font-bold text-base sm:text-2xl"
                          : index > colonIndex
                          ? "text-white font-semibold text-sm sm:text-lg"
                          : "text-gray-400"
                      }`}
                    >
                      {char}
                    </span>
                  );
                })}
                {showCursor && (
                  <span className="inline-block w-0.5 h-4 sm:h-6 bg-emerald-400 ml-1 animate-pulse"></span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleProcess;
